import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  CheckCircle2,
  Send,
  Loader2,
  Sparkles,
  Crown,
  TrendingUp,
  Users,
  Phone,
  Calendar,
  FileSignature,
  Banknote,
  Wallet,
  Percent,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable Daily Report form.
 *
 * Props:
 *   - report     : full daily_reports doc (with auto/manual/verification_pass/ceo_pass)
 *   - agentRole  : 'associate' | 'senior' (derived from report.agent_role)
 *   - onSubmit   : async function(payload) → handles POST (either /api/public/daily-report/{token} or /api/reports/daily/today/submit)
 *   - submitting : bool
 *   - readOnly   : bool (if status === 'submitted')
 */
export function DailyReportForm({ report, onSubmit, submitting, readOnly }) {
  const auto = report?.auto || {};
  const initialManual = report?.manual || {};
  const initialVp = report?.verification_pass || null;
  const initialCp = report?.ceo_pass || null;

  const [manual, setManual] = useState(initialManual);
  const [vpEnabled, setVpEnabled] = useState(!!initialVp);
  const [cpEnabled, setCpEnabled] = useState(!!initialCp);
  const [vp, setVp] = useState(initialVp || emptyPass());
  const [cp, setCp] = useState(initialCp || emptyPass());
  const [notes, setNotes] = useState(report?.notes || '');

  // ★ 2026-07-29 (hooks fix): sync local state when a DIFFERENT report is loaded.
  // We intentionally only re-run on `report?.id` change (loading a new report),
  // not on every field mutation — otherwise typing in the form would reset the
  // local state on each parent re-render. Latest values are read via closure
  // inside handleSubmit, so we don't need them in the dep list.
  useEffect(() => {
    setManual(report?.manual || {});
    setVpEnabled(!!report?.verification_pass);
    setCpEnabled(!!report?.ceo_pass);
    setVp(report?.verification_pass || emptyPass());
    setCp(report?.ceo_pass || emptyPass());
    setNotes(report?.notes || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.id]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (submitting || readOnly) return;
    try {
      await onSubmit({
        manual: sanitizeManual(manual, report?.agent_role),
        verification_pass: vpEnabled ? sanitizePass(vp) : null,
        ceo_pass: cpEnabled ? sanitizePass(cp) : null,
        notes,
      });
    } catch (err) {
      toast.error("Errore durante l'invio del report.");
    }
  };

  const role = report?.agent_role;
  const showAssociateBlock = role === 'associate' || role === 'senior_associate';
  const showSeniorBlock = role === 'senior' || role === 'senior_associate';

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="daily-report-form">
      {/* Auto-counter block */}
      <Card className="border-border bg-[var(--surface-2,#f6f3ec)]/40">
        <CardContent className="p-4 md:p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[var(--gp-gold)]" />
            <h3 className="font-semibold text-sm uppercase tracking-[0.18em] text-[var(--gp-forest,#0f5132)]">
              Auto-counter (dal CRM)
            </h3>
          </div>

          {showAssociateBlock && (
            <div className="space-y-2">
              {role === 'senior_associate' && (
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold pt-1">
                  📞 Lato Associate (cold-call)
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <StatTile icon={Calendar} label="Appuntamenti fissati" value={auto.appointments_booked || 0} testid="auto-appointments" />
                <StatTile icon={CheckCircle2} label="Presenze (call)" value={auto.attended || 0} testid="auto-attended" />
                <StatTile icon={Users} label="No-show" value={auto.no_shows || 0} testid="auto-noshows" />
              </div>
            </div>
          )}

          {showSeniorBlock && (
            <div className="space-y-2">
              {role === 'senior_associate' && (
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold pt-2">
                  🎯 Lato Senior (Zoom &amp; follow-up)
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <StatTile icon={Phone} label="Call effettuate" value={auto.calls_attended || 0} testid="auto-calls" />
                <StatTile icon={Calendar} label="Follow-up fissati" value={auto.followups_scheduled || 0} testid="auto-fu-sched" />
                <StatTile icon={CheckCircle2} label="Follow-up effettuati" value={auto.followups_attended || 0} testid="auto-fu-done" />
              </div>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground">
            Questi numeri arrivano automaticamente dal CRM (Calendly, Zoom report, activity log).
            Non sono editabili.
          </p>
        </CardContent>
      </Card>

      {/* Manual fields */}
      <Card className="border-border">
        <CardContent className="p-4 md:p-5 space-y-4">
          <div className="flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-[var(--gp-forest,#0f5132)]" />
            <h3 className="font-semibold text-sm uppercase tracking-[0.18em] text-[var(--gp-forest,#0f5132)]">
              Da compilare manualmente
            </h3>
          </div>

          {showAssociateBlock && (
            <div className="space-y-2">
              {role === 'senior_associate' && (
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                  📞 Lato Associate
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <NumField label="Numero lead" value={manual.leads_count} onChange={(v) => setManual({ ...manual, leads_count: v })} disabled={readOnly} testid="m-leads" />
                <NumField label="Numero risposte" value={manual.responses_count} onChange={(v) => setManual({ ...manual, responses_count: v })} disabled={readOnly} testid="m-responses" />
              </div>
            </div>
          )}

          {showSeniorBlock && (
            <div className="space-y-2">
              {role === 'senior_associate' && (
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold pt-2">
                  🎯 Lato Senior
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <NumField label="Proposte inviate" value={manual.proposals_sent} onChange={(v) => setManual({ ...manual, proposals_sent: v })} disabled={readOnly} testid="m-proposals" />
                <NumField label="Contratti firmati" value={manual.contracts_signed} onChange={(v) => setManual({ ...manual, contracts_signed: v })} disabled={readOnly} testid="m-contracts" />
                <EurField label="Fatturato" value={manual.revenue_eur} onChange={(v) => setManual({ ...manual, revenue_eur: v })} disabled={readOnly} testid="m-revenue" icon={TrendingUp} />
                <NumField label="N° pagamenti ricevuti" value={manual.payments_received_count} onChange={(v) => setManual({ ...manual, payments_received_count: v })} disabled={readOnly} testid="m-payments" />
                <EurField label="Incassato" value={manual.cash_collected_eur} onChange={(v) => setManual({ ...manual, cash_collected_eur: v })} disabled={readOnly} testid="m-cash" icon={Banknote} />
                <EurField label="Commissioni pagate" value={manual.commissions_paid_eur} onChange={(v) => setManual({ ...manual, commissions_paid_eur: v })} disabled={readOnly} testid="m-commissions" icon={Wallet} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Verification Pass toggle */}
      <PassCard
        title="Verification Pass"
        icon={Sparkles}
        accentClass="from-[var(--gp-gold)]/10"
        enabled={vpEnabled}
        onToggle={setVpEnabled}
        block={vp}
        onChange={setVp}
        readOnly={readOnly}
        prefix="vp"
      />

      {/* CEO Pass toggle */}
      <PassCard
        title="CEO Pass"
        icon={Crown}
        accentClass="from-[var(--gp-teal,#0e7c7b)]/10"
        enabled={cpEnabled}
        onToggle={setCpEnabled}
        block={cp}
        onChange={setCp}
        readOnly={readOnly}
        prefix="cp"
      />

      {/* Notes */}
      <Card className="border-border">
        <CardContent className="p-4 md:p-5 space-y-2">
          <Label className="text-sm font-medium">Note (opzionali)</Label>
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={readOnly}
            placeholder="Eventuali contesto, blockers, segnalazioni…"
            data-testid="report-notes"
          />
        </CardContent>
      </Card>

      {/* Submit */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <Button
          type="submit"
          disabled={submitting || readOnly}
          data-testid="report-submit"
          className="min-w-[150px]"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              Invio…
            </>
          ) : readOnly ? (
            <>
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Già inviato
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-1.5" />
              Invia report
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

// ===================== Helpers / sub-components =====================
function emptyPass() {
  return {
    invited: 0,
    attended: 0,
    contracts: 0,
    revenue_eur: 0,
    payments_count: 0,
    cash_collected_eur: 0,
  };
}

function sanitizeManual(m, role) {
  const n = (v) => Number.isFinite(+v) ? +v : 0;
  const assoc = {
    leads_count: n(m.leads_count),
    responses_count: n(m.responses_count),
  };
  const sen = {
    proposals_sent: n(m.proposals_sent),
    contracts_signed: n(m.contracts_signed),
    revenue_eur: n(m.revenue_eur),
    payments_received_count: n(m.payments_received_count),
    cash_collected_eur: n(m.cash_collected_eur),
    commissions_paid_eur: n(m.commissions_paid_eur),
  };
  if (role === 'associate') return assoc;
  if (role === 'senior_associate') return { ...assoc, ...sen };
  return sen;
}

function sanitizePass(p) {
  const n = (v) => Number.isFinite(+v) ? +v : 0;
  return {
    invited: n(p.invited),
    attended: n(p.attended),
    contracts: n(p.contracts),
    revenue_eur: n(p.revenue_eur),
    payments_count: n(p.payments_count),
    cash_collected_eur: n(p.cash_collected_eur),
  };
}

function StatTile({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl bg-[var(--surface-1)] border border-border p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="font-serif text-2xl md:text-3xl mt-0.5 text-[var(--gp-forest,#0f5132)]">{value}</div>
    </div>
  );
}

function NumField({ label, value, onChange, disabled, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input
        type="number"
        inputMode="numeric"
        min="0"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : parseInt(e.target.value || '0', 10))}
        disabled={disabled}
        data-testid={testid}
      />
    </div>
  );
}

function EurField({ label, value, onChange, disabled, testid, icon: Icon }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs inline-flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3 text-muted-foreground" />}
        {label} (€)
      </Label>
      <Input
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value || '0'))}
        disabled={disabled}
        data-testid={testid}
      />
    </div>
  );
}

function PassCard({ title, icon: Icon, enabled, onToggle, block, onChange, readOnly, prefix, accentClass }) {
  return (
    <Card className={`border-border bg-gradient-to-br ${accentClass} to-transparent`}>
      <CardContent className="p-4 md:p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-[var(--gp-forest,#0f5132)]" />
            <h3 className="font-semibold text-sm uppercase tracking-[0.18em] text-[var(--gp-forest,#0f5132)]">
              {title} <span className="text-muted-foreground font-normal normal-case text-xs">(opzionale)</span>
            </h3>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={onToggle}
            disabled={readOnly}
            data-testid={`${prefix}-toggle`}
          />
        </div>
        {enabled && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <NumField label="Invitati" value={block.invited} onChange={(v) => onChange({ ...block, invited: v })} disabled={readOnly} testid={`${prefix}-invited`} />
            <NumField label="Presenti" value={block.attended} onChange={(v) => onChange({ ...block, attended: v })} disabled={readOnly} testid={`${prefix}-attended`} />
            <NumField label="Contratti firmati" value={block.contracts} onChange={(v) => onChange({ ...block, contracts: v })} disabled={readOnly} testid={`${prefix}-contracts`} />
            <EurField label="Fatturato" value={block.revenue_eur} onChange={(v) => onChange({ ...block, revenue_eur: v })} disabled={readOnly} testid={`${prefix}-revenue`} icon={TrendingUp} />
            <NumField label="N° pagamenti" value={block.payments_count} onChange={(v) => onChange({ ...block, payments_count: v })} disabled={readOnly} testid={`${prefix}-payments`} />
            <EurField label="Incasso" value={block.cash_collected_eur} onChange={(v) => onChange({ ...block, cash_collected_eur: v })} disabled={readOnly} testid={`${prefix}-cash`} icon={Banknote} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
